import React, { useEffect, useState, useCallback } from 'react';
import { Activity, RefreshCw, Timer, Gauge } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { apiClient, PerformanceMetricsResponse } from '@/utils/apiClient';

interface PerformanceMetricsProps {
  className?: string;
}

const formatMs = (ms: number, units: { ms: string; seconds: string }): string => {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} ${units.seconds}`;
  return `${Math.round(ms)} ${units.ms}`;
};

export const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({ className }) => {
  const { t } = useTranslation();
  const at = (t as any).analytics;
  const [data, setData] = useState<PerformanceMetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getPerformanceMetrics(7);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : (at?.fetchError ?? 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [at?.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">{at?.loading ?? 'Loading...'}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-500 dark:text-red-400">{error}</span>
          <button onClick={fetchData} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {at?.refresh ?? 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const timeUnits = { ms: at?.ms ?? 'ms', seconds: at?.seconds ?? 's' };

  // Color code avg response time
  const avgColor =
    data.avg_response_time_ms < 1000
      ? 'text-green-600 dark:text-green-400'
      : data.avg_response_time_ms < 3000
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-red-600 dark:text-red-400';

  const p95Color =
    data.p95_response_time_ms < 3000
      ? 'text-green-600 dark:text-green-400'
      : data.p95_response_time_ms < 5000
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-red-600 dark:text-red-400';

  const maxDailyMs = Math.max(...data.daily_trend.map(d => d.avg_ms), 1);

  return (
    <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <Activity className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {at?.performance ?? 'Performance'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {at?.dailyTrend ?? 'Last'} {data.period_days} {at?.daysShort ?? 'd'}
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
        >
          <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
        </button>
      </div>

      {/* Headline metrics */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Timer className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">{at?.avgResponseTime ?? 'Avg Response'}</span>
          </div>
          <p className={clsx('text-xl font-bold tabular-nums', avgColor)}>
            {formatMs(data.avg_response_time_ms, timeUnits)}
          </p>
        </div>
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Gauge className="h-3.5 w-3.5 text-purple-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">{at?.p95Latency ?? 'P95 Latency'}</span>
          </div>
          <p className={clsx('text-xl font-bold tabular-nums', p95Color)}>
            {formatMs(data.p95_response_time_ms, timeUnits)}
          </p>
        </div>
      </div>

      {/* Per-model breakdown */}
      {data.by_model.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {at?.byModel ?? 'By Model'}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-1.5 text-gray-500 dark:text-gray-400 font-medium">{at?.model ?? 'Model'}</th>
                  <th className="text-right py-1.5 text-gray-500 dark:text-gray-400 font-medium">{at?.avgMs ?? 'Avg ms'}</th>
                  <th className="text-right py-1.5 text-gray-500 dark:text-gray-400 font-medium">{at?.p95Ms ?? 'P95 ms'}</th>
                  <th className="text-right py-1.5 text-gray-500 dark:text-gray-400 font-medium">{at?.requestCount ?? 'Reqs'}</th>
                </tr>
              </thead>
              <tbody>
                {data.by_model.map((model) => (
                  <tr key={model.model} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-1.5 font-medium text-gray-900 dark:text-white truncate max-w-[120px]">
                      {model.model}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {formatMs(model.avg_ms, timeUnits)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {formatMs(model.p95_ms, timeUnits)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {model.request_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Daily trend sparkline bars */}
      {data.daily_trend.length > 1 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {at?.dailyTrend ?? 'Daily Trend'}
          </h4>
          <div className="flex items-end gap-1 h-16">
            {data.daily_trend.map((day, i) => {
              const height = Math.max((day.avg_ms / maxDailyMs) * 100, 4);
              const color =
                day.avg_ms < 1000
                  ? 'bg-green-400 dark:bg-green-500 hover:bg-green-500'
                  : day.avg_ms < 3000
                  ? 'bg-yellow-400 dark:bg-yellow-500 hover:bg-yellow-500'
                  : 'bg-red-400 dark:bg-red-500 hover:bg-red-500';
              return (
                <div
                  key={i}
                  className={clsx('flex-1 rounded-t-sm transition-all', color)}
                  style={{ height: `${height}%` }}
                  title={`${day.date}: ${formatMs(day.avg_ms, timeUnits)} ${at?.avgAbbrev ?? 'avg'} (${day.request_count} ${at?.reqShort ?? 'req'})`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] text-gray-400 mt-1">
            <span>{data.daily_trend[0]?.date?.slice(5)}</span>
            <span>{data.daily_trend[data.daily_trend.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
      )}
    </div>
  );
};
